import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { DatePicker, Col, Image, Input, Rate, Row, Segmented, Select, Space, Table, Tag, Tooltip } from 'antd'
import { ShoppingOutlined, ShoppingCartOutlined, ShopOutlined, StarFilled } from '@ant-design/icons'
import controller from './reviews.controller'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import __multiple from '../../../helpers/multiple_actions/__multiple'
import { getPriceShort } from '../../../helpers/price'
import './reviews.less'

/** Цвет оценки: от красного к зелёному, чтобы плохое и хорошее читалось без чтения цифры */
export const ratingColor = (rating: number) => {
    if (rating <= 1) return '#cf1322'
    if (rating === 2) return '#d4380d'
    if (rating === 3) return '#d48806'
    if (rating === 4) return '#7cb305'

    return '#389e0d'
}

/** Звёзды с поджатыми отступами — пять штук гарантированно в одну строку */
export const RatingStars = ({ value, size = 13 }: { value: number, size?: number }) => (
    <Rate
        disabled
        value={value}
        className="reviews-rate"
        style={{ fontSize: size, color: ratingColor(value) }}
    />
)

export const statusColor = (status: string) => {
    if (status === 'published') return 'green'
    if (status === 'rejected') return 'red'

    return 'orange'
}

export const typeIcon = (entityType: string) => {
    if (entityType === 'product') return <ShoppingOutlined />
    if (entityType === 'order') return <ShoppingCartOutlined />

    return <ShopOutlined />
}

export const entityLink = (record: any) => {
    if (record.entityType === 'product') return `/shop/catalog/product/${record.entityId}`
    if (record.entityType === 'order') return `/shop/orders/${record.entityId}`

    return null
}

const dateToTimestamp = (dateString: string | string[]) => {
    if (dateString === '' || Array.isArray(dateString)) return null
    const parts = dateString.split('.')

    return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime() / 1000
}

const formatDate = (value: string) => value
    ? new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(value))
    : '—'

const StatTile = ({ value, label, color, icon }: any) => (
    <Col xs={12} md={6}>
        <div className="reviews-stat">
            <div className="reviews-stat__value" style={color ? { color } : undefined}>
                {icon}{value}
            </div>
            <div className="reviews-stat__label">{label}</div>
        </div>
    </Col>
)

export const ReviewsList = observer(() => {

    const history = useHistory()
    const location = useLocation()

    useEffect(() => {
        // Из карточки пользователя приходят с ?user_id= — сразу только его отзывы
        const userId = Number(new URLSearchParams(location.search).get('user_id'))
        if (userId > 0) controller.setFilter('user_id', userId)
        else controller.get()

        __multiple.setActions([
            { label: i.t('reviews:actions.approve'), value: 'approve', action: () => controller.multipleModerate('approve') },
            { label: i.t('reviews:actions.reject'), value: 'reject', action: () => controller.multipleModerate('reject'), danger: true },
            { label: i.t('def:flag_read'), value: 'read', action: () => controller.multipleRead() },
            { label: i.t('def:delete_elements'), value: 'delete', action: () => controller.multipleDelete(), danger: true },
        ])
    }, [])

    const buttons = [
        { title: i.t('users:excelExport'), click: () => controller.export('xlsx'), type: 'dashed' },
        { title: i.t('users:csvExport'), click: () => controller.export('csv'), type: 'dashed' },
    ]

    const columns = [
        {
            // Номер отзыва ссылкой — видно, что запись открывается
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            sorter: true,
            width: 80,
            render: (id: number, record: any) => (
                <Space size={6}>
                    <Tooltip title={record.viewed ? '' : i.t('reviews:notViewed')}>
                        <span style={{
                            width: 7, height: 7, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                            background: record.viewed ? 'transparent' : '#52c41a'
                        }} />
                    </Tooltip>
                    <Link to={`/shop/reviews/${id}`}>#{id}</Link>
                </Space>
            )
        },
        {
            title: i.t('reviews:fields.type'),
            key: 'entityType',
            width: 140,
            render: (text: any, record: any) => {
                // Заказ уже есть в колонке «Заказ» — ссылку оставляем только у товара
                const link = record.entityType === 'product' ? entityLink(record) : null
                const label = i.t(`reviews:types.${record.entityType}`)

                return (
                    <Space size={6}>
                        <Tag icon={typeIcon(record.entityType)} style={{ margin: 0 }}>{label}</Tag>
                        {link && <a href={link} target="_blank" rel="noreferrer">#{record.entityId}</a>}
                    </Space>
                )
            }
        },
        {
            title: i.t('reviews:fields.rating'),
            dataIndex: 'rating',
            key: 'rating',
            sorter: true,
            width: 110,
            render: (rating: number) => <RatingStars value={rating} />
        },
        {
            title: i.t('reviews:fields.order'),
            key: 'order',
            width: 210,
            render: (text: any, record: any) => record.order ? (
                <Space size={4} wrap>
                    <a href={`/shop/orders/${record.order.id}`} target="_blank" rel="noreferrer">#{record.order.id}</a>
                    <span>{getPriceShort(record.order.sum)}</span>
                    {record.order.deliveryType && (
                        <Tag style={{ margin: 0 }}>
                            {i.t(record.order.deliveryType === 2 ? 'reviews:delivery.pickup' : 'reviews:delivery.delivery')}
                        </Tag>
                    )}
                </Space>
            ) : <span className="reviews-muted">—</span>
        },
        {
            title: i.t('reviews:fields.place'),
            key: 'place',
            width: 130,
            render: (text: any, record: any) => (
                <span className={record.order?.placeName || record.order?.warehouseName ? '' : 'reviews-muted'}>
                    {record.order?.placeName ?? record.order?.warehouseName ?? '—'}
                </span>
            )
        },
        {
            title: i.t('reviews:fields.user'),
            key: 'userId',
            width: 150,
            render: (text: any, record: any) => (
                <Space size={4} wrap>
                    <a href={`/users/${record.userId}`} target="_blank" rel="noreferrer">
                        {record.userName ?? record.userId}
                    </a>
                    {record.isVerifiedPurchase && (
                        <Tooltip title={i.t('reviews:fields.verified')}>
                            <Tag color="blue" style={{ margin: 0 }}>✓</Tag>
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: i.t('reviews:fields.status'),
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            width: 150,
            render: (status: string, record: any) => (
                <Space size={4} wrap>
                    <Tag color={statusColor(status)} style={{ margin: 0 }}>{i.t(`reviews:statuses.${status}`)}</Tag>
                    {record.pendingRevision && <Tag color="purple" style={{ margin: 0 }}>{i.t('reviews:hasRevision')}</Tag>}
                </Space>
            )
        },
        {
            title: i.t('reviews:fields.date'),
            dataIndex: 'createdAt',
            key: 'created_at',
            sorter: true,
            width: 120,
            render: (text: any) => formatDate(text)
        },
    ]

    return (
        <>
            <Titles
                title={`${i.t('reviews:title')}${controller.notViewed > 0 ? ` · ${i.t('reviews:notViewed')} ${controller.notViewed}` : ''}`}
                buttons={buttons}
                breadcrumb={[{ title: i.t('reviews:title') }]}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                <Segmented
                    block
                    value={controller.filters.status ?? 'all'}
                    onChange={(value: any) => controller.setFilter('status', value === 'all' ? null : value)}
                    options={[
                        { label: i.t('reviews:tabs.all'), value: 'all' },
                        { label: i.t('reviews:statuses.pending'), value: 'pending' },
                        { label: i.t('reviews:statuses.published'), value: 'published' },
                        { label: i.t('reviews:statuses.rejected'), value: 'rejected' },
                    ]}
                />

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    <Select
                        allowClear
                        style={{ width: 150 }}
                        placeholder={i.t('reviews:fields.type')}
                        value={controller.filters.entity_type}
                        onChange={e => controller.setFilter('entity_type', e)}
                        options={[
                            { label: i.t('reviews:types.product'), value: 'product' },
                            { label: i.t('reviews:types.order'), value: 'order' },
                            { label: i.t('reviews:types.shop'), value: 'shop' },
                        ]}
                    />
                    <Select
                        allowClear
                        style={{ width: 110 }}
                        placeholder={i.t('reviews:fields.rating')}
                        value={controller.filters.rating}
                        onChange={e => controller.setFilter('rating', e)}
                        options={[5, 4, 3, 2, 1].map(v => ({ label: `${v} ★`, value: v }))}
                    />
                    <Select
                        allowClear
                        style={{ width: 120 }}
                        placeholder={i.t('reviews:fields.photos')}
                        value={controller.filters.has_photo}
                        onChange={e => controller.setFilter('has_photo', e)}
                        options={[
                            { label: i.t('def:yes'), value: 1 },
                            { label: i.t('def:no'), value: 0 },
                        ]}
                    />
                    <DatePicker.RangePicker
                        format="DD.MM.YYYY"
                        style={{ width: 240 }}
                        placeholder={[i.t('def:dateFrom'), i.t('def:dateTo')]}
                        onChange={(dates, dateStrings) => {
                            controller.setFilters({
                                date_from: dateToTimestamp(dateStrings[0]),
                                date_to: dateToTimestamp(dateStrings[1]),
                            })
                        }}
                    />
                    <Input
                        allowClear
                        type="number"
                        style={{ width: 130 }}
                        placeholder={`${i.t('reviews:fields.user')} ID`}
                        value={controller.filters.user_id ?? ''}
                        onChange={e => controller.setFilter('user_id', e.target.value === '' ? null : Number(e.target.value))}
                    />
                    <Input.Search
                        allowClear
                        style={{ width: 260 }}
                        placeholder={i.t('reviews:fields.searchPlaceholder')}
                        onSearch={value => controller.setFilter('search', value === '' ? null : value)}
                    />
                </div>
            </div>

            {controller.stats && (
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    <StatTile value={controller.stats.total} label={i.t('reviews:stats.total')} />
                    <StatTile value={controller.stats.today > 0 ? `+${controller.stats.today}` : '0'} label={i.t('reviews:stats.today')} />
                    <StatTile
                        value={controller.stats.ratingAvg > 0 ? controller.stats.ratingAvg.toFixed(2) : '—'}
                        label={i.t('reviews:stats.ratingAvg')}
                        color={controller.stats.ratingAvg > 0 ? ratingColor(Math.round(controller.stats.ratingAvg)) : undefined}
                        icon={<StarFilled />}
                    />
                    <StatTile
                        value={controller.stats.pending}
                        label={i.t('reviews:stats.pending')}
                        color={controller.stats.pending > 0 ? '#d48806' : undefined}
                    />
                </Row>
            )}

            <Table
                rowKey="id"
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: __multiple.selected,
                    onSelect: (record: any, selected: any, selectedRows: any) => __multiple.set(selectedRows),
                    onSelectAll: (selected: any, selectedRows: any) => __multiple.set(selectedRows),
                }}
                columns={columns}
                dataSource={controller.list}
                scroll={{ x: 1000 }}
                expandable={{
                    // Текст отзыва не влезает в колонку — показываем его строкой во всю
                    // ширину под записью. Строки раскрыты всегда, стрелку прячем.
                    showExpandColumn: false,
                    expandedRowKeys: controller.list.map((el: any) => el.id),
                    expandedRowClassName: () => 'reviews-row-text',
                    expandedRowRender: (record: any) => (
                        <div
                            style={{ cursor: 'pointer' }}
                            onClick={(event) => {
                                const target = event.target as HTMLElement
                                if (target.closest('a, button, .ant-image, .ant-image-preview-root')) return

                                history.push(`/shop/reviews/${record.id}`)
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {record.files?.length > 0 && (
                                    <Image.PreviewGroup>
                                        <Space size={4}>
                                            {record.files.map((file: any) => (
                                                <Image
                                                    key={file.id}
                                                    src={file.url}
                                                    width={26}
                                                    height={26}
                                                    style={{ objectFit: 'cover', borderRadius: 5 }}
                                                />
                                            ))}
                                        </Space>
                                    </Image.PreviewGroup>
                                )}
                                <span className={`reviews-oneline${record.comment ? '' : ' reviews-muted'}`}>
                                    {record.comment ?? i.t('reviews:noComment')}
                                </span>
                            </div>
                            {record.replyText && (
                                <div className="reviews-oneline reviews-muted" style={{ marginTop: 4 }}>
                                    {i.t('reviews:reply.title')}: {record.replyText}
                                </div>
                            )}
                        </div>
                    ),
                }}
                onRow={(record: any) => ({
                    style: { cursor: 'pointer' },
                    onClick: (event: React.MouseEvent) => {
                        // Клик по ссылке, чекбоксу или превью фото не должен уводить со страницы
                        const target = event.target as HTMLElement
                        if (target.closest('a, button, .ant-checkbox-wrapper, .ant-image, .ant-image-preview-root')) return

                        history.push(`/shop/reviews/${record.id}`)
                    }
                })}
                pagination={{
                    position: ['bottomLeft'],
                    hideOnSinglePage: true,
                    current: Number(controller.pagination.page),
                    pageSize: controller.pagination.limit,
                    total: controller.pagination.total,
                    showSizeChanger: false,
                    onChange: (page) => controller.setPage(page)
                }}
                onChange={(pagination, filters, sorter, extra) => {
                    if (extra.action === 'sort') controller.get(sorter)
                }}
            />
        </>
    )
})
